import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { ModuleData, ModuleProgress } from '../../types';
import { Card } from '../ui/Card';
import { ProgressRing } from '../ui/ProgressRing';
import { CheckCircle, Sparkles, ArrowRight, Target, BookOpen, Play, Star, Trophy } from 'lucide-react';
import * as Icons from 'lucide-react';
interface ModuleCardProps {
  module: ModuleData;
  progress: ModuleProgress;
  onClick: () => void;
  onCheatSheetClick?: (e: React.MouseEvent) => void;
}
export function ModuleCard({
  module,
  progress,
  onClick,
  onCheatSheetClick
}: ModuleCardProps) {
  const IconComponent = (Icons as any)[module.icon] || Icons.HelpCircle;
  const isMastered = progress.status === 'mastered';
  const hasReviewItems = progress.reviewNeededCount > 0;
  // Dynamic status text based on actual progress (Growth Mindset)
  const getStatusText = () => {
    if (isMastered) return 'Meester! 🏆';
    if (progress.masteryPercentage === 0) return 'Start hier';
    if (progress.masteryPercentage < 30) return 'Goed bezig';
    if (progress.masteryPercentage < 60) return 'Ga zo door';
    if (progress.masteryPercentage < 100) return 'Bijna daar!';
    return 'Klaar voor actie';
  };
  // Get status color
  const getStatusColor = () => {
    if (isMastered) return 'text-amber-500';
    if (progress.masteryPercentage === 0) return 'text-slate-400';
    if (progress.masteryPercentage < 60) return 'text-blue-600';
    return 'text-indigo-600';
  };
  const handleCheatSheetClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCheatSheetClick) {
      onCheatSheetClick(e);
    }
  };
  return <motion.div className="relative h-full group" whileHover={{
    scale: 1.02,
    y: -5
  }} whileTap={{
    scale: 0.98
  }} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 20
  }} data-id="element-348">
      {/* Review Badge */}
      {hasReviewItems && <motion.div initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} className="absolute -top-3 -right-3 z-20 bg-rose-500 text-white px-4 py-1.5 rounded-full shadow-lg flex items-center gap-2 border-4 border-slate-50" role="status" aria-label={`${progress.reviewNeededCount} vragen om te herhalen`} data-id="element-349">
          <Target className="w-4 h-4" aria-hidden="true" data-id="element-350" />
          <span className="text-sm font-bold" data-id="element-351">
            {progress.reviewNeededCount}
          </span>
        </motion.div>}

      {/* Golden glow for mastered */}
      {isMastered && <motion.div className="absolute inset-0 rounded-3xl z-0" animate={{
      boxShadow: ['0 0 20px rgba(251, 191, 36, 0.2)', '0 0 40px rgba(251, 191, 36, 0.4)', '0 0 20px rgba(251, 191, 36, 0.2)']
    }} transition={{
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut'
    }} aria-hidden="true" data-id="element-352" />}

      <Card hoverEffect className={`relative h-full flex flex-col cursor-pointer overflow-hidden border-2 transition-all duration-300 ${isMastered ? 'bg-gradient-to-br from-amber-50/50 to-white border-amber-200' : 'bg-white border-transparent hover:border-indigo-100'}`} role="article" aria-label={`${module.title} module, ${progress.masteryPercentage}% compleet`} data-id="element-353">
        <div className="p-8 flex-1 flex flex-col" onClick={onClick} role="button" tabIndex={0} onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }} aria-label={`Start ${module.title} oefensessie`} data-id="element-354">
          {/* Header Section */}
          <div className="flex justify-between items-start mb-6" data-id="element-355">
            <motion.div className={`p-4 rounded-2xl bg-slate-50 ${module.color} relative shadow-sm group-hover:shadow-md transition-shadow`} animate={isMastered ? {
            rotate: [0, -5, 5, -5, 0]
          } : {}} transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 3
          }} aria-hidden="true" data-id="element-356">
              <IconComponent className="w-10 h-10" data-id="element-357" />
              {isMastered && <motion.div className="absolute -top-2 -right-2" animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360]
            }} transition={{
              duration: 3,
              repeat: Infinity
            }} data-id="element-358">
                  <Sparkles className="w-6 h-6 text-amber-500 fill-amber-400" data-id="element-359" />
                </motion.div>}
            </motion.div>

            <div className="transform scale-110" data-id="element-360">
              <ProgressRing progress={progress.masteryPercentage} size={60} strokeWidth={6} color={isMastered ? 'text-amber-400' : module.color} data-id="element-361" />
            </div>
          </div>

          {/* Content Section */}
          <div className="mb-6 flex-1" data-id="element-362">
            <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-indigo-700 transition-colors" data-id="element-363">
              {module.title}
            </h3>
            <p className="text-slate-600 text-base leading-relaxed line-clamp-3" data-id="element-364">
              {module.description}
            </p>
          </div>

          {/* Status Section */}
          <div className="mt-auto" data-id="element-365">
            <div className="flex items-center justify-between bg-slate-50 rounded-xl p-3 mb-2" data-id="element-366">
              <span className={`text-sm font-bold ${getStatusColor()} flex items-center gap-2`} data-id="element-367">
                {isMastered ? <Trophy className="w-4 h-4" aria-hidden="true" data-id="element-368" /> : <Star className="w-4 h-4" aria-hidden="true" data-id="element-369" />}
                {getStatusText()}
              </span>
              <div className="text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-370">
                {progress.masteryPercentage}% Compleet
              </div>
            </div>
          </div>
        </div>

        {/* ✅ IMPROVED: Action Bar with ARIA labels */}
        <div className="flex border-t border-slate-100 bg-slate-50/50 mt-auto" data-id="element-371">
          <motion.button whileHover={{
          backgroundColor: 'rgba(99, 102, 241, 0.1)'
        }} whileTap={{
          scale: 0.95
        }} onClick={handleCheatSheetClick} aria-label={`Open uitleg voor ${module.title}`} className="flex-1 flex items-center justify-center gap-2 py-4 text-sm font-bold text-indigo-600 hover:text-indigo-700 transition-colors min-h-[48px]" data-id="element-372">
            <BookOpen className="w-5 h-5" aria-hidden="true" data-id="element-373" />
            <span data-id="element-374">Uitleg</span>
          </motion.button>

          <div className="w-px bg-slate-200 my-2" aria-hidden="true" data-id="element-375" />

          <motion.button whileHover={{
          backgroundColor: 'rgba(16, 185, 129, 0.1)'
        }} whileTap={{
          scale: 0.95
        }} onClick={onClick} aria-label={`Start ${module.title} oefensessie`} className="flex-1 flex items-center justify-center gap-2 py-4 text-sm font-bold text-emerald-600 hover:text-emerald-700 transition-colors min-h-[48px]" data-id="element-376">
            <Play className="w-5 h-5 fill-current" aria-hidden="true" data-id="element-377" />
            <span data-id="element-378">Start</span>
          </motion.button>
        </div>

        {/* Progress Bar Bottom */}
        <div className="h-1.5 w-full bg-slate-100" role="progressbar" aria-valuenow={progress.masteryPercentage} aria-valuemin={0} aria-valuemax={100} aria-label={`${progress.masteryPercentage}% van ${module.title} voltooid`} data-id="element-379">
          <motion.div className={`h-full ${isMastered ? 'bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400' : 'bg-indigo-600'}`} initial={{
          width: 0
        }} animate={{
          width: `${progress.masteryPercentage}%`
        }} transition={{
          duration: 1,
          ease: 'easeOut'
        }} data-id="element-380" />
        </div>
      </Card>
    </motion.div>;
}